/**
 * Рольова сцена: детермінований рушій ветвистих діалогів (див. D-005).
 *
 * Два режими: «обери відповідь» — впізнавання, і «скажи сам» — вільне введення
 * з нечітким порівнянням. Другий значно складніший, тому відкривається кнопкою.
 */
import { useEffect, useMemo, useState } from 'react'
import { useStore } from '../lib/store.ts'
import { kv } from '../lib/db.ts'
import { matchAnswer } from '../lib/text.ts'
import { Speak, TopBar } from '../components/ui.tsx'
import type { RoleplayOption } from '../lib/course.ts'

interface Turn {
  nodeId: string
  said: string
  ok: boolean
  feedback?: string
}

export function RoleplayView({ moduleId, id }: { moduleId: string; id: string }) {
  const { idx } = useStore()
  const m = idx.moduleById.get(moduleId)
  const rp = m?.roleplays.find((r) => r.id === id)

  const [nodeId, setNodeId] = useState(rp?.start ?? '')
  const [history, setHistory] = useState<Turn[]>([])
  const [free, setFree] = useState(false)
  const [input, setInput] = useState('')
  const [pending, setPending] = useState<{ option: RoleplayOption; note?: string } | null>(null)
  const [finished, setFinished] = useState(false)

  const node = useMemo(() => rp?.nodes.find((n) => n.id === nodeId), [rp, nodeId])

  // Пройдена сцена зараховується як станція екзамену-симуляції.
  useEffect(() => {
    if (!finished || !rp) return
    void kv.get<string[]>('roleplaysDone').then((done) => {
      const next = new Set(done ?? [])
      next.add(rp.id)
      return kv.set('roleplaysDone', [...next])
    }).catch(() => undefined)
  }, [finished, rp])
  if (!m || !rp) return <div className="screen">Сцену не знайдено</div>

  const restart = () => {
    setNodeId(rp.start)
    setHistory([])
    setPending(null)
    setFinished(false)
    setInput('')
  }

  const commit = (option: RoleplayOption, saidText: string, extraNote?: string) => {
    setHistory((h) => [...h, { nodeId, said: saidText, ok: option.ok, feedback: option.feedback ?? extraNote }])
    setPending(null)
    setInput('')
    if (option.next) setNodeId(option.next)
    else setFinished(true)
  }

  const checkFree = () => {
    if (!node) return
    const accepted = node.accept ?? node.options.filter((o) => o.ok).map((o) => o.pl)
    const result = matchAnswer(input, accepted)
    const best = node.options.find((o) => o.pl === result.best) ?? node.options.find((o) => o.ok)!
    if (result.correct) {
      commit(best, input.trim(), result.quality === 'exact' ? undefined : result.hint)
    } else {
      setPending({ option: best, note: `Найближчий правильний варіант: ${best.pl}` })
    }
  }

  const mistakes = history.filter((h) => !h.ok).length

  return (
    <>
      <TopBar title={rp.title} />
      <div className="screen stack">
        <div className="card stack--tight stack">
          <div className="row">
            <span style={{ fontSize: 26 }} aria-hidden>🎭</span>
            <div>
              <div style={{ fontWeight: 600 }}>{rp.role}</div>
              <p className="small muted">{rp.goal}</p>
            </div>
          </div>
          <button
            className={`chip${free ? ' chip--good' : ''}`}
            style={{ alignSelf: 'flex-start', padding: '7px 13px', fontSize: 13 }}
            onClick={() => { setFree((v) => !v); setPending(null) }}
          >
            {free ? '⌨️ режим «скажи сам»' : '☑︎ режим «обери відповідь»'}
          </button>
        </div>

        {history.map((turn, i) => {
          const n = rp.nodes.find((x) => x.id === turn.nodeId)
          return (
            <div className="stack--tight stack" key={i}>
              {n?.npc && (
                <div className="line">
                  <Speak audio={n.npc.audio} text={n.npc.pl} small />
                  <div className="bubble">
                    <div className="bubble__pl">{n.npc.pl}</div>
                    <div className="bubble__uk">{n.npc.uk}</div>
                  </div>
                </div>
              )}
              <div className="line line--you">
                <div className="bubble">
                  <div className="bubble__pl">{turn.said}</div>
                  {turn.feedback && (
                    <div className={`feedback feedback--${turn.ok ? 'hint' : 'no'}`} style={{ marginTop: 8 }}>
                      {turn.feedback}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {!finished && node && (
          <div className="stack">
            {node.npc && (
              <div className="line">
                <Speak audio={node.npc.audio} text={node.npc.pl} small auto />
                <div className="bubble">
                  <div className="bubble__pl">{node.npc.pl}</div>
                  <div className="bubble__uk">{node.npc.uk}</div>
                </div>
              </div>
            )}

            <div className="feedback feedback--hint">🎯 {node.task}</div>

            {free ? (
              <div className="stack--tight stack">
                <input
                  className="input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && input.trim() && checkFree()}
                  placeholder="Твоя відповідь польською…"
                  autoCapitalize="off" autoCorrect="off" spellCheck={false}
                />
                {pending && (
                  <>
                    <div className="feedback feedback--no">{pending.note}</div>
                    <button className="btn btn--wide" onClick={() => commit(pending.option, pending.option.pl)}>
                      Прийняти правильний варіант і продовжити
                    </button>
                  </>
                )}
                <button className="btn btn--primary btn--wide" disabled={!input.trim()} onClick={checkFree}>
                  Сказати
                </button>
              </div>
            ) : (
              <div className="stack--tight stack">
                {node.options.map((o, i) => (
                  <button key={i} className="option" onClick={() => commit(o, o.pl)}>
                    <div style={{ fontWeight: 550 }}>{o.pl}</div>
                    <div className="option__uk">{o.uk}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {finished && (
          <div className="card center stack">
            <div style={{ fontSize: 44 }}>{mistakes === 0 ? '🏅' : '✅'}</div>
            <h2>Сцену пройдено</h2>
            <p className="muted small">
              {mistakes === 0
                ? 'Жодної невдалої репліки — саме так це й звучить у житті.'
                : `Невдалих реплік: ${mistakes}. Подивись пояснення вище й пройди ще раз.`}
            </p>
            <button className="btn btn--primary btn--wide" onClick={restart}>Пройти ще раз</button>
            {!free && (
              <button className="btn btn--wide" onClick={() => { restart(); setFree(true) }}>
                Тепер без підказок — «скажи сам»
              </button>
            )}
            <a className="btn btn--ghost btn--wide" href={`#/module/${m.id}`}>Назад до модуля</a>
          </div>
        )}
      </div>
    </>
  )
}
